import {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Route, Routes } from 'react-router-dom';
import { initializeSessionFromRedirect } from '../../apiservice/interceptor';
import { setItem } from '../../auth/localStorageManager';
import { sessionManager } from '../../auth/sessionManager';
import { validateSession } from '../../auth/validateSession';
import {
  diagnosisHistoryLink,
  errorLink,
  remoteDiagnosisLink,
} from '../../common/links';
import Loader from '../../components/common/Loader';
import { useLoader } from '../../context/LoaderContext';
import { RedirectParameterContext } from '../../context/RedirectParameterContext';
import useBeforeUnload from '../../hooks/useBeforeUnload';
import useQueryParams from '../../hooks/useQueryParams';
import { handleBeforeUnload } from '../../utils/utilFunctions';
import RootPage from './RootPage';

// Lazy load page components for code splitting
const DiagnosisHistory = lazy(() => import('../DiagnosisHistory'));
const ErrorPage = lazy(() => import('../Error'));
const RemoteDiagnosis = lazy(
  () => import('../RemoteDiagnosis/RemoteDiagnosis')
);

const BaseLayout = ({ platform }) => {
  const params = new URLSearchParams(window.location.search);
  const { getAllParams } = useQueryParams();
  const urlParams = getAllParams();
  const { redirectParameters } = useContext(RedirectParameterContext);

  useBeforeUnload(() => handleBeforeUnload(urlParams, redirectParameters));

  const token = params.get('accessToken');
  const { isLoading } = useLoader();

  const [isTokenValid, setisTokenValid] = useState(false);

  const validateToken = useCallback(token => {
    validateSession(token).then(valid => {
      if (!valid) {
        sessionManager.cleanupAndRedirect();
      } else {
        setisTokenValid(true);
      }
    });
  }, []);

  const init = useCallback(token => {
    initializeSessionFromRedirect(); //set in session storage
    validateToken(token);
    setItem('user_id', '');
  }, []);

  useEffect(() => {
    if (token) init(token);
  }, [token]);

  return (
    <>
      <Loader isOpen={isLoading} />
      <Routes>
        <Route path="/" element={<RootPage />} />
        {isTokenValid && (
          <>
            <Route
              path={remoteDiagnosisLink}
              element={
                <Suspense>
                  <RemoteDiagnosis platform={platform} />
                </Suspense>
              }
            />
            <Route
              path={diagnosisHistoryLink}
              element={
                <Suspense>
                  <DiagnosisHistory platform={platform} />
                </Suspense>
              }
            />
          </>
        )}
        <Route
          path={errorLink}
          element={
            <Suspense>
              <ErrorPage />
            </Suspense>
          }
        />
      </Routes>
    </>
  );
};

export default BaseLayout;
